import type { DateRangePresetName } from "@racoon/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Job,
  JobStatus,
  JobType,
  JobCategory,
  DispatcherReviewStatus,
  ConnectProto,
} from "@racoon/api";
import { useT, Namespace } from "@racoon/i18n";
import { getEnumName } from "@racoon/lib";
import { createEnumFilters, type Option } from "~/shared/lib/createEnumFilters";
import { getTypeMap, getJobCategoryMap, getJobStatusMap } from "~/shared/lib/translationMaps";
import { useDisabledJobTypes } from "~/shared/hooks/useDisabledJobTypes";

// Accept both REST and gRPC Job types
type JobInput = Job | ConnectProto.Job;

/**
 * The two views the Orders screen switches between — separate pages, not filters.
 * Rendered as the top row of tabs.
 */
export type OrderViewFilter = "registry" | "job-map";

export const ORDER_VIEWS: Option<OrderViewFilter>[] = [
  { key: "Карта заявок", value: "job-map" },
  { key: "Реестр заданий", value: "registry" },
];

/**
 * Status narrowing *inside* the registry view, rendered as pills under the controls
 * row. "all" means no narrowing at all.
 */
export type OrderStatusFilter = "all" | "active" | JobStatus;

export const ORDER_STATUSES: Option<OrderStatusFilter>[] = [
  { key: "Все", value: "all" },
  { key: "Неназначенные", value: JobStatus.JOB_STATUS_CREATED },
  { key: "Назначенные", value: JobStatus.JOB_STATUS_ASSIGNED },
  { key: "В процессе", value: "active" },
  { key: "Выполненные", value: JobStatus.JOB_STATUS_COMPLETED },
  { key: "Отмененные", value: JobStatus.JOB_STATUS_CANCELLED },
  { key: "Невывоз", value: JobStatus.JOB_STATUS_NOT_COMPLETED },
];

/**
 * Job statuses that a single filter option stands for. Only statuses that share a
 * label need an entry — everything else matches itself.
 */
export const JOB_STATUS_GROUPS: Partial<Record<JobStatus, JobStatus[]>> = {
  [JobStatus.JOB_STATUS_COMPLETED]: [
    JobStatus.JOB_STATUS_COMPLETED,
    JobStatus.JOB_STATUS_FORCE_COMPLETED,
  ],
};

// The category tabs' order in the design; "all" leads.
const CATEGORY_TAB_ORDER: string[] = [
  "all",
  JobCategory.JOB_CATEGORY_CO,
  JobCategory.JOB_CATEGORY_MSW,
  JobCategory.JOB_CATEGORY_TIPPER,
  JobCategory.JOB_CATEGORY_LIQUID,
  JobCategory.JOB_CATEGORY_DISINFECTION,
];
const categoryTabRank = (value: unknown) => {
  const rank = CATEGORY_TAB_ORDER.indexOf(String(value));
  return rank < 0 ? CATEGORY_TAB_ORDER.length : rank;
};

/** "В процессе" covers a truck that is on its way and one already working on site. */
const ACTIVE_JOB_STATUSES = [JobStatus.JOB_STATUS_ENROUTE, JobStatus.JOB_STATUS_ON_SERVICE];

/**
 * Job statuses a status pill stands for. Empty for "Все", which doesn't narrow by
 * status at all. Shared by the server-side query and the local filter below.
 */
export const getStatusJobStatuses = (value: OrderStatusFilter): JobStatus[] => {
  if (value === "all") return [];
  if (value === "active") return ACTIVE_JOB_STATUSES;
  return JOB_STATUS_GROUPS[value] ?? [value];
};

/**
 * A status pill's badge off ListJobs' "status" facet (keyed by enum number): the sum
 * over the statuses the pill stands for ("Все" counts every status), kept to those of
 * `narrowedTo` — the status dropdown's — when it is given.
 */
export const statusPillCount = (
  counts: ConnectProto.FacetCounts["counts"],
  value: OrderStatusFilter,
  narrowedTo?: JobStatus[],
): number => {
  const pill = getStatusJobStatuses(value);
  return Object.entries(counts).reduce((sum, [number, n]) => {
    const name = getEnumName(ConnectProto.JobStatusSchema, Number(number)) as JobStatus | undefined;
    const isIn = (statuses: JobStatus[]) => name !== undefined && statuses.includes(name);
    return (pill.length === 0 || isIn(pill)) && (!narrowedTo || isIn(narrowedTo)) ? sum + n : sum;
  }, 0);
};

/**
 * Which of the job's time columns the date picker narrows on. The values are the
 * snake_case proto field names `DateTimeFilter.field` accepts — the backend
 * rejects anything else, so a value off the URL is checked against this list.
 */
export type OrderDateField = "tw_start_at" | "fact_end_at" | "created_at";

const ORDER_DATE_FIELDS: OrderDateField[] = ["tw_start_at", "fact_end_at", "created_at"];

/** The window the registry has always filtered on, kept as the default. */
export const DEFAULT_ORDER_DATE_FIELD: OrderDateField = "tw_start_at";

/** The order map opens on the orders submitted that day. */
export const DEFAULT_JOB_MAP_DATE_FIELD: OrderDateField = "created_at";

export const isOrderDateField = (value: string | null): value is OrderDateField =>
  ORDER_DATE_FIELDS.some((field) => field === value);

/** The date-field options as the calendar's field picker takes them, translated. */
export const useOrderDateFieldOptions = () => {
  const t = useT();
  return useMemo(
    () =>
      ORDER_DATE_FIELDS.map((field) => ({
        id: field,
        name: t(Namespace.Orders, `filters.dateField.${field}`),
      })),
    [t],
  );
};

export type PhotoFilterValue = "all" | "no_photo" | "uploaded" | DispatcherReviewStatus | null;

export const PHOTO_FILTERS: Option<PhotoFilterValue>[] = [
  { key: "Без фото", value: "no_photo" },
  {
    key: "Подтвержденные",
    value: DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_APPROVED,
  },
  {
    key: "Отклоненные",
    value: DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_REJECTED,
  },
  {
    key: "Подозрительные",
    value: DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_SUSPICIOUS,
  },
  { key: "Загруженные", value: "uploaded" },
];

/** helpers */
/** The photo dropdown as ListJobs request fields. */
export const photoFilterToRequest = (
  photo: Option<PhotoFilterValue> | null,
): { hasPhotoReport?: boolean } => {
  switch (photo?.value) {
    case "no_photo":
      return { hasPhotoReport: false };
    case "uploaded":
      return { hasPhotoReport: true };
    default:
      return {};
  }
};

export const isReviewPhotoFilter = (photo: Option<PhotoFilterValue> | null) =>
  photo?.value === DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_APPROVED ||
  photo?.value === DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_REJECTED ||
  photo?.value === DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_SUSPICIOUS;

const uniqById = <T extends { id: number }>(arr: (T | undefined)[]) => {
  const seen = new Set<number>();
  return arr.filter((x): x is T => {
    if (!x) return false;
    if (seen.has(x.id)) return false;
    seen.add(x.id);
    return true;
  });
};

const toNum = (v: unknown): number | undefined => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "string") {
    const n = parseInt(v, 10);
    return isNaN(n) ? undefined : n;
  }
  if (typeof v === "number") return isNaN(v) ? undefined : v;
  return undefined;
};

const hasAnyPhoto = (j: JobInput) => {
  const jobAny = j as any; // TODO(refactor): Remove 'as any' cast once the old REST endpoint is deprecated.
  const serviceStartedReportUrl =
    jobAny.serviceStartedReportUrl || jobAny.service_started_report_url;
  const serviceEndedReportUrl = jobAny.serviceEndedReportUrl || jobAny.service_ended_report_url;
  // service_started/ended_report_url are deprecated in favor of file_reports
  const fileReports = jobAny.file_reports ?? jobAny.fileReports ?? [];
  return Boolean(serviceStartedReportUrl || serviceEndedReportUrl || fileReports.length > 0);
};

/** main hook */
const filterOrders = <J extends JobInput>(
  orders: J[],
  params: {
    searchValue: string;
    status: Option<OrderStatusFilter>;
    photo: Option<PhotoFilterValue> | null;
    jobType: Option<JobType> | null;
    jobStatus?: Option<JobStatus> | null;
    selectedPickupLocationIds: number[];
    selectedServiceReceiverIds: number[];
    selectedDriverIds: number[];
    selectedPickupLocationGroupIds: number[];
  },
) => {
  const {
    searchValue,
    status,
    photo,
    jobType,
    jobStatus,
    selectedPickupLocationIds,
    selectedServiceReceiverIds,
    selectedDriverIds,
    selectedPickupLocationGroupIds,
  } = params;

  return orders.filter((o) => {
    const orderAny = o as any; // TODO(refactor): Remove 'as any' cast once the old REST endpoint is deprecated.
    // Handle both camelCase (gRPC) and snake_case (REST) field names
    const serviceReceiver = orderAny.serviceReceiver ?? orderAny.service_receiver;
    const pickupLocation = orderAny.pickupLocation ?? orderAny.pickup_location;
    const driverOnShift = orderAny.driverOnShift ?? orderAny.driver_on_shift;
    let dispatcherReviewStatus =
      orderAny.dispatcherReviewStatus ?? orderAny.dispatcher_review_status;
    // Convert numeric enum (gRPC) to string name (REST) for comparison
    if (typeof dispatcherReviewStatus === "number") {
      dispatcherReviewStatus = ConnectProto.DispatcherReviewStatusSchema.values.find(
        (v) => v.number === dispatcherReviewStatus,
      )?.name;
    }

    // search filter
    if (!jobMatchesSearch(orderAny, searchValue)) return false;

    // status pill - handle both string (REST) and number (gRPC) enums
    const pillStatuses = getStatusJobStatuses(status.value);
    if (pillStatuses.length > 0) {
      // Convert numeric status to string name if needed
      let jobStatusName = orderAny.status;
      if (typeof orderAny.status === "number") {
        jobStatusName = ConnectProto.JobStatusSchema.values.find(
          (v) => v.number === orderAny.status,
        )?.name;
      }

      if (!pillStatuses.includes(jobStatusName)) return false;
    }

    // job status (dropdown filter) - independent of the pill above
    if (jobStatus?.value && (jobStatus.value as string) !== "all") {
      let jobStatusName = orderAny.status;
      if (typeof orderAny.status === "number") {
        jobStatusName = ConnectProto.JobStatusSchema.values.find(
          (v) => v.number === orderAny.status,
        )?.name;
      }

      const allowed = JOB_STATUS_GROUPS[jobStatus.value] ?? [jobStatus.value];
      if (!allowed.includes(jobStatusName)) return false;
    }

    // pickup locations - normalize id (string | number | bigint) for comparison
    if (selectedPickupLocationIds.length > 0) {
      const plIdNum = toNum(pickupLocation?.id);
      if (!plIdNum || !selectedPickupLocationIds.includes(plIdNum)) {
        return false;
      }
    }

    // service receivers (organizations) - normalize id for comparison
    if (selectedServiceReceiverIds.length > 0) {
      const srIdNum = toNum(serviceReceiver?.id);
      if (!srIdNum || !selectedServiceReceiverIds.includes(srIdNum)) {
        return false;
      }
    }

    // drivers - normalize id for comparison
    if (selectedDriverIds.length > 0) {
      const driverIdNum = toNum(driverOnShift?.driver?.id);
      if (!driverIdNum || !selectedDriverIds.includes(driverIdNum)) {
        return false;
      }
    }

    // pickup location groups (route groups) - jobs created before the
    // group-based flow carry no group and never match this filter.
    if (selectedPickupLocationGroupIds.length > 0) {
      const groupIdNum = toNum(orderAny.pickupLocationGroupId ?? orderAny.pickup_location_group_id);
      if (!groupIdNum || !selectedPickupLocationGroupIds.includes(groupIdNum)) {
        return false;
      }
    }

    // photo filter
    switch (photo?.value) {
      case "all":
        break; // no filter
      case "no_photo":
        if (hasAnyPhoto(o)) return false;
        break;
      case "uploaded":
        if (!hasAnyPhoto(o)) return false;
        break;
      case DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_APPROVED:
      case DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_REJECTED:
      case DispatcherReviewStatus.DISPATCHER_REVIEW_STATUS_SUSPICIOUS:
        if (dispatcherReviewStatus !== photo?.value) return false;
        // most often confirmation/rejection implies photos exist:
        if (!hasAnyPhoto(o)) return false;
        break;
      default:
        break;
    }

    // jobType filter - handle both string (REST) and number (gRPC) enums
    if (jobType?.value && (jobType?.value as string) !== "all") {
      if (jobTypeName(orderAny) !== jobType.value) {
        return false;
      }
    }

    return true;
  });
};

/**
 * The dropdown options and the client-side filtering read different sets on purpose:
 * options may draw on a wider source so a choice is not limited to what is on screen,
 * while filtering must stay on the rows the page actually renders — mixing the two
 * would surface rows the server filters had already excluded. `filterSource` defaults
 * to `optionSource` for callers that legitimately use one set for both.
 */
/** A full BIN: twelve digits. Anything shorter is read as a job number. */
export const isBinQuery = (query: string): boolean => /^\d{12}$/.test(query.trim());

/**
 * The search field as list filters the server takes. The text search matches the job
 * id, the customer's name and the pickup location's name; what else the field names is
 * resolved here:
 * - a full BIN filters by the customers with it, among `customers` (the company's
 *   counterparties), within the customer filter when one is set;
 * - an order number as the page writes it (O123, or a Cyrillic О123) filters by it;
 * - a job number as the page writes it (Z123, or a Cyrillic З123) searches its id.
 */
export const searchRequest = (
  query: string,
  customers: readonly { id: bigint | string; bin?: string }[] | undefined,
  customerFilter: readonly bigint[],
): { search?: string; serviceReceiverIds?: bigint[]; orderNumbers?: string[] } => {
  const text = query.trim();
  if (!text) return {};
  if (isBinQuery(text) && customers) {
    const ids = customers
      .filter((customer) => customer.bin?.trim() === text)
      .map((customer) => BigInt(customer.id));
    if (ids.length > 0) {
      const within =
        customerFilter.length > 0 ? ids.filter((id) => customerFilter.includes(id)) : ids;
      // 0 is no customer's id: a BIN outside the picked customers lists nothing.
      return { serviceReceiverIds: within.length > 0 ? within : [0n] };
    }
  }
  const orderNumber = /^[oо](\d+)$/i.exec(text);
  if (orderNumber) return { orderNumbers: [`O${orderNumber[1]}`] };
  const jobNumber = /^[zз](\d+)$/i.exec(text);
  if (jobNumber) return { search: jobNumber[1] };
  return { search: text };
};

/**
 * Whether a job matches the search field in the browser (the review mode, which filters
 * its whole match here): what the field says it searches, the pickup location, the
 * customer and its BIN, the order number and the job number (Z, or Cyrillic З, allowed;
 * О for the order number's O too).
 */
export const jobMatchesSearch = (job: any, query: string): boolean => {
  const text = query.toLowerCase().trim();
  if (!text) return true;
  const receiver = job.serviceReceiver ?? job.service_receiver;
  const location = job.pickupLocation ?? job.pickup_location;
  const number = text.replace(/^з/, "z").replace(/^о/, "o");
  return (
    [receiver?.name, location?.name, receiver?.bin].some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(text),
    ) ||
    String(job.id).includes(number.replace(/^z/, "")) ||
    String(job.orderNumber ?? job.order_number ?? "")
      .toLowerCase()
      .includes(number)
  );
};

/** The orders page's date presets, in the order its date filters list them. */
export const ORDER_DATE_PRESETS: DateRangePresetName[] = [
  "last2",
  "last7",
  "last14",
  "last30",
  "thisWeek",
  "lastWeek",
  "thisMonth",
  "lastMonth",
];

/** A job's type as its enum name, whether the job carries it as a name (REST) or a number. */
export const jobTypeName = (job: { type?: unknown }): string | undefined =>
  typeof job.type === "number"
    ? ConnectProto.JobTypeSchema.values.find((v) => v.number === job.type)?.name
    : (job.type as string | undefined);

/**
 * Whether the type filter offers a type: one the organization has on, or one turned off
 * in settings that the page's jobs have or that is the selected filter.
 */
export const isJobTypeOffered = (
  name: string,
  disabled: ReadonlySet<ConnectProto.JobType>,
  onPage: ReadonlySet<string>,
  selected: string | null,
): boolean =>
  !disabled.has(
    ConnectProto.JobType[name.replace(/^JOB_TYPE_/, "") as keyof typeof ConnectProto.JobType],
  ) ||
  onPage.has(name) ||
  name === selected;

export const useOrderFilters = <J extends JobInput>(
  optionSource: J[],
  searchParams: URLSearchParams,
  patch: (changes: Record<string, string | null>) => void,
  status: Option<OrderStatusFilter>,
  filterSource: J[] = optionSource,
) => {
  const t = useT();

  const disabledJobTypes = useDisabledJobTypes();

  // Types and categories the organization turned off are not offered as filters, but a
  // type the page's jobs have, or the one selected, still is: the filter is scoped to the
  // page, not to the settings.
  const selectedJobType = searchParams.get("jobType");
  const pageJobTypes = useMemo(
    () => new Set(optionSource.map(jobTypeName).filter((name) => name !== undefined)),
    [optionSource],
  );
  const JOB_TYPE_FILTERS = useMemo(
    () =>
      createEnumFilters<JobType>(getTypeMap(t), {
        excludeKeys: [JobType.JOB_TYPE_UNDEFINED, JobType.UNRECOGNIZED],
      }).filter((option) =>
        isJobTypeOffered(
          String(option.value),
          disabledJobTypes.types,
          pageJobTypes,
          selectedJobType,
        ),
      ),
    [t, disabledJobTypes, pageJobTypes, selectedJobType],
  );

  const JOB_STATUS_FILTERS = useMemo(
    () => createEnumFilters<JobStatus>(getJobStatusMap(t) as Record<string, string>),
    [t],
  );

  const JOB_CATEGORIES = useMemo(
    () =>
      createEnumFilters<JobCategory>(getJobCategoryMap(t), {
        excludeKeys: [JobCategory.JOB_CATEGORY_UNDEFINED, JobCategory.UNRECOGNIZED],
        prepend: [{ key: t(Namespace.Orders, "jobMap.allJobTypes"), value: "all" }],
      })
        .filter(
          (option) => !disabledJobTypes.categories.has(String(option.value).replace(/^JOB_/, "")),
        )
        .sort((a, b) => categoryTabRank(a.value) - categoryTabRank(b.value)),
    [t, disabledJobTypes],
  );

  // URL-driven filters
  const photoVal = searchParams.get("photo");
  const photo = useMemo<Option<PhotoFilterValue> | null>(
    () => (photoVal ? PHOTO_FILTERS.find((p) => String(p.value) === photoVal) || null : null),
    [photoVal],
  );
  const setPhoto = (val: Option<PhotoFilterValue> | null) =>
    patch({ photo: val ? String(val.value) : null, page: null });

  const jobTypeVal = searchParams.get("jobType");
  const jobType = useMemo<Option<JobType> | null>(
    () =>
      jobTypeVal
        ? (JOB_TYPE_FILTERS.find((jt) => String(jt.value) === jobTypeVal) as Option<JobType>) ||
          null
        : null,
    [jobTypeVal, JOB_TYPE_FILTERS],
  );
  const setJobType = (val: Option<JobType> | null) =>
    patch({ jobType: val ? String(val.value) : null, page: null });

  // Kept under its own URL key: "status" already belongs to the tab above.
  const jobStatusVal = searchParams.get("jobStatus");
  const jobStatus = useMemo<Option<JobStatus> | null>(
    () =>
      jobStatusVal
        ? (JOB_STATUS_FILTERS.find((js) => String(js.value) === jobStatusVal) as
            | Option<JobStatus>
            | undefined) || null
        : null,
    [jobStatusVal, JOB_STATUS_FILTERS],
  );
  const setJobStatus = (val: Option<JobStatus> | null) =>
    patch({ jobStatus: val ? String(val.value) : null, page: null });

  // searchQuery is what the URL (and the server request) holds; searchValue echoes
  // keystrokes immediately and patches the URL after a pause, so the backend is not
  // queried per keystroke. The map view filters on the echo and stays instant.
  const searchQuery = searchParams.get("search") ?? "";
  const [searchValue, setSearchValue] = useState(searchQuery);
  useEffect(() => {
    setSearchValue(searchQuery);
  }, [searchQuery]);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);
  const onSearchChange = (value: string) => {
    setSearchValue(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      patch({ search: value.trim() ? value : null, page: null });
    }, 1000);
  };

  // Helpers for array states in URL
  const getNumArray = (key: string) => {
    const val = searchParams.get(key);
    if (!val) return [];
    return val
      .split(",")
      .map(Number)
      .filter((n) => !isNaN(n));
  };
  const setNumArray = (key: string, arr: number[]) =>
    patch({ [key]: arr.length ? arr.join(",") : null, page: null });

  const selectedPickupLocationIds = getNumArray("pickup");
  const setSelectedPickupLocationIds = (arr: number[]) => setNumArray("pickup", arr);

  const selectedServiceReceiverIds = getNumArray("receiver");
  const setSelectedServiceReceiverIds = (arr: number[]) => setNumArray("receiver", arr);

  const selectedDriverIds = getNumArray("driver");
  const setSelectedDriverIds = (arr: number[]) => setNumArray("driver", arr);

  const selectedPickupLocationGroupIds = getNumArray("group");
  const setSelectedPickupLocationGroupIds = (arr: number[]) => setNumArray("group", arr);

  // unique sources for dropdowns
  const pickupLocations = useMemo(() => {
    return uniqById(
      optionSource.map((o) => {
        const oAny = o as any;
        return oAny.pickupLocation || oAny.pickup_location;
      }),
    );
  }, [optionSource]);

  const serviceReceivers = useMemo(() => {
    return uniqById(
      optionSource.map((o) => {
        const oAny = o as any;
        return oAny.serviceReceiver || oAny.service_receiver;
      }),
    );
  }, [optionSource]);

  const drivers = useMemo(() => {
    return uniqById(
      optionSource
        .map((o) => {
          const oAny = o as any;
          const driverOnShift = oAny.driverOnShift || oAny.driver_on_shift;
          return driverOnShift?.driver;
        })
        .filter(Boolean),
    );
  }, [optionSource]);

  // filtering
  const filteredOrders = useMemo(() => {
    return filterOrders(filterSource, {
      searchValue,
      status,
      photo,
      jobType,
      jobStatus,
      selectedPickupLocationIds,
      selectedServiceReceiverIds,
      selectedDriverIds,
      selectedPickupLocationGroupIds,
    });
  }, [
    filterSource,
    searchValue,
    status,
    photo,
    jobType,
    jobStatus,
    selectedPickupLocationIds,
    selectedServiceReceiverIds,
    selectedDriverIds,
    selectedPickupLocationGroupIds,
  ]);

  return {
    /** selected filters + setters */
    /** selected filters + setters */
    photo,
    setPhoto,
    jobType,
    setJobType,
    jobStatus,
    setJobStatus,
    selectedPickupLocationIds,
    setSelectedPickupLocationIds,
    selectedServiceReceiverIds,
    setSelectedServiceReceiverIds,
    selectedDriverIds,
    setSelectedDriverIds,
    selectedPickupLocationGroupIds,
    setSelectedPickupLocationGroupIds,
    onSearchChange,
    searchValue,
    searchQuery,

    /** options for UI */
    ORDER_STATUSES,
    PHOTO_FILTERS,
    JOB_TYPE_FILTERS, // job type filter options
    JOB_STATUS_FILTERS, // job status filter options
    JOB_CATEGORIES, // job category filter options
    pickupLocations, // unique pickup locations (PickupLocation[])
    serviceReceivers, // unique service receivers (Organization[])
    drivers, // unique drivers (Driver[])

    /** result */
    filteredOrders,
  };
};
